//src/pages/Materials/MaterialUpload.js
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { createMaterial, getMaterial } from '../../api/material/MaterialApi';
import { getProjects } from '../../api/project/projectApi';
import { useNavigate } from 'react-router-dom';
import Loader from '../../components/common/Loader/Loader';
import { FaInfoCircle } from 'react-icons/fa';
import { IoIosCloseCircle } from 'react-icons/io';
import { MdAddBox } from 'react-icons/md';

const MAX_IMAGES = 20;
const MAX_FILE_SIZE = 50 * 1024 * 1024;
const DOCUMENT_EXTENSIONS = ['pdf', 'doc', 'docx', 'ppt', 'pptx', 'txt'];
const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'tiff', 'tif', 'heic', 'heif', 'ico'];

const getExt = (filename) => filename.split('.').pop().toLowerCase();

const MaterialUpload = ({ project, onClose }) => {
  const [projects, setProjects] = useState([]);
  const [project_id, setProject_id] = useState('');
  const [material_name, setMaterial_name] = useState('');
  const [material_files, setMaterial_files] = useState([]);
  const [material_file_name, setmaterial_file_name] = useState(null);
  const [materialList, setMaterialList] = useState([]);
  const [projectname, setProjectName] = useState('');
  const [loading, setLoading] = useState(false);
  const [nameTooltip, setNameTooltip] = useState(false);
  const [fileTooltip, setFileTooltip] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const headingRef = useRef(null);
  const fileInputRef = useRef(null);
  const dragCounter = useRef(0); // avoids flicker when dragging over child elements

  const navigate = useNavigate();
  const user_id = localStorage.getItem('user_id');

  useEffect(() => {
    if (headingRef.current) {
      headingRef.current.focus();
    }
  }, []);


  useEffect(() => {
    const appRoot = document.getElementById('root');
    const prevOverflow = document.body.style.overflow;

    document.body.style.overflow = 'hidden';
    if (appRoot) {
      appRoot.setAttribute('inert', '');          
      appRoot.setAttribute('aria-hidden', 'true');
    }

    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && !loading) onClose?.();
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = prevOverflow;
      if (appRoot) {
        appRoot.removeAttribute('inert');
        appRoot.removeAttribute('aria-hidden');
      }
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, loading]);

  useEffect(() => {
    setProject_id(project?.project_id);
    setProjectName(project?.project_name);
  }, [project]);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await getProjects(user_id);
        setProjects(response.data.projects);
      } catch (err) {
        console.error('Error Fetching assessment:', err);
      }
    };
    fetchProjects();
  }, [user_id]);

  useEffect(() => {
    const fetchMaterials = async () => {
      if (project_id) {
        try {
          const response = await getMaterial(user_id, project_id);
          setMaterialList(response.data.data || []);
        } catch (error) {
          console.error('Error fetching materials:', error);
        }
      }
    };
    fetchMaterials();
  }, [user_id, project_id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!project_id || !material_name || material_files.length === 0) {
      alert('Please fill in all fields');
      return;
    }

    const isDuplicate = materialList.some(
      (material) => material.material_name === material_name
    );
    if (isDuplicate) {
      alert('Material name already exists. Please choose a different name.');
      return;
    }

    if (loading) return;
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('project_id', project_id);
      formData.append('user_id', user_id);
      formData.append('material_name', material_name);
      material_files.forEach((file) => {
        formData.append('material_file', file);
      });
      formData.append('material_file_name', material_file_name);

      const response = await createMaterial(formData);
      if (response.status === 200 || response.status === 201) {
        alert('Material uploaded successfully');
        setmaterial_file_name('');
        setMaterial_files([]);
        const updatedMaterials = await getMaterial(user_id, project_id);
        setMaterialList(updatedMaterials.data.data || []);
        navigate(`/material_list/${project_id}`);
      }
    } catch (error) {
      if (error.response && error.response.status === 403) {
        alert('Maximum number of materials reached for this assessment.');
      } else if (error.response && error.response.status === 400) {
        alert(error.response.data?.message || 'Failed to upload material');
      } else {
        console.error('Error uploading material:', error);
        alert('Failed to upload material');
      }
    } finally {
      setLoading(false);
    }
  };

  // ---- Shared validation for both the file picker and drag-and-drop ----
  const processFiles = (fileList) => {
    const selected = Array.from(fileList || []);
    if (selected.length === 0) return false;

    const firstExt = getExt(selected[0].name);
    const isDocumentBatch = DOCUMENT_EXTENSIONS.includes(firstExt);
    const isImageBatch = IMAGE_EXTENSIONS.includes(firstExt);

    if (!isDocumentBatch && !isImageBatch) {
      alert('Unsupported file type');
      return false;
    }

    if (isDocumentBatch && selected.length > 1) {
      alert('Only one document can be uploaded at a time. To upload images instead, select up to 20 image files.');
      return false;
    }

    if (isImageBatch && selected.length > MAX_IMAGES) {
      alert(`You can upload a maximum of ${MAX_IMAGES} images at a time.`);
      return false;
    }

    const allSameCategory = selected.every((file) => {
      const ext = getExt(file.name);
      return isDocumentBatch ? DOCUMENT_EXTENSIONS.includes(ext) : IMAGE_EXTENSIONS.includes(ext);
    });

    if (!allSameCategory) {
      alert('Please upload only one file type at a time (all documents, or all images).');
      return false;
    }

    const oversized = selected.find((file) => file.size > MAX_FILE_SIZE);
    if (oversized) {
      alert('File size: not more than 50MB');
      return false;
    }

    setMaterial_files(selected);
    setmaterial_file_name(selected.map((f) => f.name).join(', '));
    return true;
  };

  const handleFileChange = (e) => {
    processFiles(e.target.files);
    e.target.value = ''; // allows re-selecting the same file
  };

  // ---- Drag and drop handlers ----
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    if (e.dataTransfer?.types?.includes('Files')) setIsDragging(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setIsDragging(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragging(false);
    processFiles(e.dataTransfer.files);
  };

  // Stops the browser from opening a file dropped outside the drop zone
  const blockStrayDrop = (e) => {
    e.preventDefault();
  };

  const removeFiles = () => {
    setMaterial_files([]);
    setmaterial_file_name('');
  };

  const toggleNameTooltip = () => setNameTooltip(!nameTooltip);
  const toggleFileTooltip = () => setFileTooltip(!fileTooltip);

  const modal = (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 overflow-y-auto"
      onDragOver={blockStrayDrop}
      onDrop={blockStrayDrop}
      onMouseDown={(e) => {
        // click on the dark backdrop closes the modal
        if (e.target === e.currentTarget && !loading) onClose?.();
      }}
    >
      {loading ? (
        <div className="w-full h-full flex items-center justify-center">
          <Loader
            aria-label="Uploading your file... this may take up to 5 minutes"
            message="Uploading your file... this may take up to 5 minutes."
          />
        </div>
      ) : (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="material-upload-title"
          className="relative overflow-hidden rounded-3xl w-11/12 md:w-8/12 lg:w-6/12 mx-auto bg-white shadow-2xl border border-gray-100 my-auto"
        >
          <div className="bg-gradient-to-br from-[#36369f] via-[#4b4bbd] to-[#2b2b80] p-5 text-white relative overflow-hidden">
            <div className="absolute inset-0 opacity-30">
              <div className="absolute -top-10 -left-10 w-40 h-40 bg-white/20 rounded-full blur-2xl"></div>
            </div>

            <div className="flex items-center justify-between relative z-10">
              <h1
                id="material-upload-title"
                ref={headingRef}
                tabIndex="-1"
                className="text-lg md:text-xl font-bold outline-none"
              >
                {projectname}
              </h1>

              <button
                type="button"
                onClick={onClose}
                aria-label="Close upload dialog"
                className="text-2xl text-white hover:text-red-300"
              >
                <IoIosCloseCircle />
              </button>
            </div>
          </div>

          <div className="p-6 md:p-8">
            <form onSubmit={handleSubmit} className="items-center rounded w-full mx-auto">
              <label className="block md:mb-2 mb-1 font-semibold md:text-base text-sm">
                Material Name:
                <FaInfoCircle
                  aria-label="information icon - Use this field to identify the topic of the learning material"
                  className="inline ml-2 text-blue-500 cursor-pointer"
                  onClick={toggleNameTooltip}
                />
              </label>
              {nameTooltip && (
                <div className="bg-[rgba(177,194,244,0.52)] md:p-2 p-1 rounded-lg md:text-sm text-xs text-black mb-2">
                  Use this field to identify the topic of the learning material
                </div>
              )}

              <input
                type="text"
                value={material_name}
                onChange={(e) => setMaterial_name(e.target.value)}
                placeholder="Enter material name"
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-white/80 backdrop-blur-sm focus:ring-2 focus:ring-[#36369f] focus:border-[#36369f]
          hover:border-[#36369f]/40 shadow-inner transition-all duration-300 outline-none"
              />

              <label className="block md:mt-6 mt-3 md:mb-2 mb-1 font-semibold md:text-base text-sm">
                Upload Document or Images:
                <FaInfoCircle
                  aria-label="information icon - File size: not more than 50MB per file. Upload one document, or up to 20 images. Max 3 materials per assessment"
                  className="inline ml-2 text-blue-500 cursor-pointer"
                  onClick={toggleFileTooltip}
                />
              </label>
              {fileTooltip && (
                <div className="bg-[rgba(177,194,244,0.52)] md:p-2 p-1 rounded-lg md:text-sm text-xs text-purple-700 mb-2">
                  File size: not more than 50MB per file<br />
                  Upload either one document (PDF, Word, PPT, TXT) or up to 20 images<br />
                  Cannot mix documents and images in the same upload<br />
                  Max 3 files per assessment
                </div>
              )}

              {/* Drop zone */}
              <div
                role="button"
                tabIndex={0}
                aria-label="Upload files. Click or press Enter to browse, or drag and drop files here"
                onClick={() => fileInputRef.current?.click()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    fileInputRef.current?.click();
                  }
                }}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`flex flex-col items-center justify-center w-full border-2 border-dashed rounded-lg p-6 cursor-pointer transition-all duration-200 outline-none focus:ring-2 focus:ring-[#36369f] ${
                  isDragging
                    ? 'border-[#36369f] bg-[#36369f]/10 scale-[1.02]'
                    : 'border-gray-300 hover:border-[#36369f]'
                }`}
              >
                <span className={`text-sm pointer-events-none ${isDragging ? 'text-[#36369f] font-semibold' : 'text-gray-600'}`}>
                  {isDragging
                    ? 'Drop your file(s) here'
                    : 'Click to upload or drag file(s) here — 1 document or up to 20 images'}
                </span>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif,.bmp,.webp,.svg,.tiff,.tif,.heic,.heif,.ico"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </div>

              {material_files.length > 0 && (
                <div className="mt-2 flex items-start justify-between gap-2 text-xs text-gray-600 text-left">
                  <span>
                    {material_files.length} file{material_files.length > 1 ? 's' : ''} selected:{' '}
                    {material_files.map((f) => f.name).join(', ')}
                  </span>
                  <button
                    type="button"
                    onClick={removeFiles}
                    className="text-red-500 hover:underline shrink-0"
                  >
                    Clear
                  </button>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className={`group relative overflow-hidden flex items-center justify-center gap-3 mx-auto px-8 py-2.5 rounded-lg mt-4 text-white font-semibold shadow-md transition-all duration-300 ${
                  loading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-[#36369f] to-[#4b4bbd] hover:scale-105'
                }`}
              >
                Upload Material
                <MdAddBox aria-hidden="true" className="md:text-lg text-base text-black" />
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );

  // Render outside #root so the background can be made inert while this stays usable
  return createPortal(modal, document.body);
};

export default MaterialUpload;